import { TelemetryFields, TelemetryValue } from './types';

const MAX_FIELD_LENGTH = 256;
const DYNAMIC_PARENT_SEGMENTS = new Set([
  'accounts',
  'activities',
  'cognitoUsers',
  'events',
  'friendlyNames',
  'instruments',
  'messages',
  'orders',
  'positions',
  'reactions',
  'users',
]);

const EMAIL_PATTERN = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g;
const SECRET_PATTERN = /\b(Bearer\s+|token[=:]\s*|password[=:]\s*)[^\s&,]+/gi;
const URL_QUERY_PATTERN = /(https?:\/\/[^\s?]+)\?[^\s]+/gi;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function redactTelemetryText(value: string): string {
  return value
    .replace(URL_QUERY_PATTERN, '$1?[redacted]')
    .replace(SECRET_PATTERN, '$1[redacted]')
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, MAX_FIELD_LENGTH);
}

export function sanitizeTelemetryFields(fields: TelemetryFields): Readonly<Record<string, TelemetryValue>> {
  const sanitized: Record<string, TelemetryValue> = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (typeof value === 'string') sanitized[key] = redactTelemetryText(value);
    else if (typeof value === 'number' && Number.isFinite(value)) sanitized[key] = value;
    else if (typeof value === 'boolean') sanitized[key] = value;
  });
  return sanitized;
}

function isDynamicSegment(segment: string, previousSegment?: string): boolean {
  if (previousSegment && DYNAMIC_PARENT_SEGMENTS.has(previousSegment)) return true;
  if (UUID_PATTERN.test(segment) || /^\d+$/.test(segment)) return true;
  if (segment.includes('@') || segment.length > 32) return true;
  return false;
}

export function normalizePath(value: string): string {
  let pathname: string;
  try {
    pathname = new URL(value, globalThis.location?.origin ?? 'https://app.alphietrader.com').pathname;
  } catch {
    pathname = value.split(/[?#]/, 1)[0];
  }

  const segments = pathname.split('/').filter(Boolean);
  const normalized = segments.map((segment, index) => {
    let decoded = segment;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      // Keep the encoded segment so malformed input cannot break telemetry.
    }
    return isDynamicSegment(decoded, segments[index - 1]) ? ':id' : decoded;
  });

  return `/${normalized.join('/')}` || '/';
}

export function normalizeError(error: unknown): TelemetryFields {
  const record = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : undefined;
  const httpStatus = typeof record?.httpStatus === 'number' && Number.isFinite(record.httpStatus) ? record.httpStatus : undefined;
  const responseCorrelationId =
    typeof record?.responseCorrelationId === 'string'
      ? record.responseCorrelationId
      : typeof record?.correlationId === 'string'
        ? record.correlationId
        : undefined;
  const rawMessage = error instanceof Error ? error.message : typeof record?.message === 'string' ? record.message : String(error);
  // The shared API helper includes the response body after this prefix. Keep the
  // useful status while preventing arbitrary server payloads from entering RUM.
  const message = httpStatus === undefined ? rawMessage : `Server returned HTTP ${httpStatus}`;
  const errorType =
    error instanceof Error
      ? error.name || 'Error'
      : typeof record?.name === 'string' && record.name
        ? record.name
        : httpStatus === undefined
          ? 'UnknownError'
          : 'HttpError';

  return {
    errorMessage: redactTelemetryText(message),
    errorType: redactTelemetryText(errorType),
    httpStatus,
    responseCorrelationId: responseCorrelationId ? redactTelemetryText(responseCorrelationId) : undefined,
  };
}
